import {
    CursorArrowRaysIcon,
    PencilIcon,
    Square2StackIcon,
} from '@heroicons/react/24/outline';
import './GeofenceToolbar.css';

const TOOLS = [
    { id: 'drag', label: 'Drag — move and reshape boundaries' },
    { id: 'draw', label: 'Draw polygon — click to place vertices, double-click to finish' },
    { id: 'basic', label: 'Add basic polygon — drop a default rectangle' },
    { id: 'select-vertex', label: 'Select vertex — edit or delete a corner' },
    { id: 'select-polygon', label: 'Select polygon — select a whole section' },
    { id: 'entrance', label: 'Place entrance marker' },
    { id: 'exit', label: 'Place exit marker' },
];

function VertexSelectIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <circle cx="12" cy="12" r="3" fill="currentColor" />
            <path d="M4 12h5M15 12h5M12 4v5M12 15v5" />
        </svg>
    );
}

function PolygonSelectIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M6 8l6-4 6 4v8l-6 4-6-4V8z" />
        </svg>
    );
}

function EntrancePinIcon() {
    return (
        <svg className="geofence-tool-icon-pin" viewBox="0 0 16 20" aria-hidden>
            <path
                d="M8 0C5.35 0 3.2 2.15 3.2 4.8c0 3.6 4.8 12.2 4.8 12.2s4.8-8.6 4.8-12.2C12.8 2.15 10.65 0 8 0z"
                fill="#16A34A"
            />
            <text
                x="8"
                y="6.5"
                textAnchor="middle"
                dominantBaseline="central"
                fill="#fff"
                fontFamily="Arial,sans-serif"
                fontSize="7"
                fontWeight="900"
            >
                E
            </text>
        </svg>
    );
}

function ExitPinIcon() {
    return (
        <svg className="geofence-tool-icon-pin" viewBox="0 0 16 20" aria-hidden>
            <path
                d="M8 0C5.35 0 3.2 2.15 3.2 4.8c0 3.6 4.8 12.2 4.8 12.2s4.8-8.6 4.8-12.2C12.8 2.15 10.65 0 8 0z"
                fill="#CC0000"
            />
            <text
                x="8"
                y="6.5"
                textAnchor="middle"
                dominantBaseline="central"
                fill="#fff"
                fontFamily="Arial,sans-serif"
                fontSize="7"
                fontWeight="900"
            >
                X
            </text>
        </svg>
    );
}

function toolIcon(id) {
    switch (id) {
        case 'drag':
            return <CursorArrowRaysIcon />;
        case 'draw':
            return <PencilIcon />;
        case 'basic':
            return <Square2StackIcon />;
        case 'select-vertex':
            return <VertexSelectIcon />;
        case 'select-polygon':
            return <PolygonSelectIcon />;
        case 'entrance':
            return <EntrancePinIcon />;
        case 'exit':
            return <ExitPinIcon />;
        default:
            return null;
    }
}

export default function GeofenceToolbar({
    activeTool,
    onChangeTool,
    layerCount = 0,
    activeLayer = 'all',
    onChangeLayer,
    hasAiBoundary = true,
}) {
    const showLayers = layerCount > 1;

    // Wrap the explainer + toolbar in a single flex container so parent
    // grid/flex layouts that expect one child don't break. The Fragment
    // shape previously here surfaced two siblings and could scramble
    // layout on the four mount points (SPW Step 2, SPW geofence modal,
    // live-drawer, bulk sidebar).
    return (
        <div className="geofence-toolbar-shell">
            {hasAiBoundary ? (
                <p className="geofence-toolbar-explainer">
                    <strong>Establish Truck Parking Area &amp; Access.</strong>{' '}
                    Click a parking area to select it, then choose{' '}
                    <strong>Delete</strong> to remove any areas that shouldn&rsquo;t
                    be included. Drag the white boundary points to adjust the
                    remaining boundaries, or draw a new boundary if needed. Place the
                    green <strong>Entrance</strong> pin and red <strong>Exit</strong>{' '}
                    pin where drivers should enter and leave your property.
                </p>
            ) : (
                <p className="geofence-toolbar-explainer">
                    <strong>Draw Truck Parking Area &amp; Access.</strong>{' '}
                    Click <strong>Draw Boundary</strong> to outline your truck
                    parking area. Draw additional boundaries if needed, then place
                    the green <strong>Entrance</strong> pin and red{' '}
                    <strong>Exit</strong> pin where drivers should enter and leave
                    your property.
                </p>
            )}
            <div className="geofence-toolbar-root" role="toolbar" aria-label="Geofence editing tools">
                {showLayers && (
                <>
                    <select
                        className="geofence-layer-select"
                        aria-label="Active layer"
                        value={activeLayer === 'all' ? 'all' : String(activeLayer)}
                        onChange={(e) => {
                            const v = e.target.value;
                            onChangeLayer(v === 'all' ? 'all' : Number(v));
                        }}
                    >
                        <option value="all">All layers</option>
                        {Array.from({ length: layerCount }, (_, i) => (
                            <option key={i} value={String(i)}>
                                {`Layer ${i + 1}`}
                            </option>
                        ))}
                    </select>
                    <div className="geofence-divider" aria-hidden />
                </>
            )}

            <button
                type="button"
                className={`geofence-tool-btn${activeTool === 'drag' ? ' active' : ''}`}
                aria-label={TOOLS[0].label}
                title={TOOLS[0].label}
                aria-pressed={activeTool === 'drag'}
                onClick={() => onChangeTool('drag')}
            >
                {toolIcon('drag')}
            </button>

            <div className="geofence-divider" aria-hidden />

            {TOOLS.slice(1, 3).map((tool) => (
                <button
                    key={tool.id}
                    type="button"
                    className={`geofence-tool-btn${activeTool === tool.id ? ' active' : ''}`}
                    aria-label={tool.label}
                    title={tool.label}
                    aria-pressed={activeTool === tool.id}
                    onClick={() => onChangeTool(tool.id)}
                >
                    {toolIcon(tool.id)}
                </button>
            ))}

            <div className="geofence-divider" aria-hidden />

            {TOOLS.slice(3, 5).map((tool) => (
                <button
                    key={tool.id}
                    type="button"
                    className={`geofence-tool-btn${activeTool === tool.id ? ' active' : ''}`}
                    aria-label={tool.label}
                    title={tool.label}
                    aria-pressed={activeTool === tool.id}
                    onClick={() => onChangeTool(tool.id)}
                >
                    {toolIcon(tool.id)}
                </button>
            ))}

            <div className="geofence-divider" aria-hidden />

            {TOOLS.slice(5).map((tool) => (
                <button
                    key={tool.id}
                    type="button"
                    className={`geofence-tool-btn${activeTool === tool.id ? ' active' : ''}`}
                    aria-label={tool.label}
                    title={tool.label}
                    aria-pressed={activeTool === tool.id}
                    onClick={() => onChangeTool(tool.id)}
                >
                    {toolIcon(tool.id)}
                </button>
            ))}
            </div>
        </div>
    );
}
